// CustomizeBalancesTab — HR Dashboard "Customize Leave Balances" sub-tab.
//
// Shows the SAME computed balance that both employee and admin see
// (via /api/admin/leave-inventory/employees). Clicking any cell opens an
// inline adjustment popover that POSTs an admin_adjustment delta transaction.
// The balance updates immediately on save because the shared helper reads
// those transactions as the single source of truth for manual adjustments.
//
// Previously the inline input POSTed to /api/admin/leave-allocations with
// {allocation: diff} on every onChange keystroke — which 404'd silently and
// re-rendered the stale server value, giving the illusion of "random negative
// numbers". This component replaces that broken flow entirely.
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import adminApi from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

export default function CustomizeBalancesTab() {
  const [data, setData] = useState({ employees: [], policies: [] });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { emp, policy, current, delta, reason }
  const [saving, setSaving] = useState(false);

  const fetchBalances = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get('/api/admin/leave-inventory/employees');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load leave balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  const openEditor = (emp, policy) => {
    const current = emp.balances?.[policy.code] || {};
    setEditing({
      emp,
      policy,
      current,
      delta: '',
      reason: '',
    });
  };

  const applyAdjustment = async () => {
    if (!editing) return;
    const delta = parseFloat(editing.delta);
    if (!isFinite(delta) || delta === 0) {
      toast.error('Enter a non-zero adjustment (positive to add, negative to subtract)');
      return;
    }
    if (!editing.reason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }
    setSaving(true);
    try {
      await adminApi.post('/api/admin/leave-allocations', {
        employee_id: editing.emp.employee_id,
        leave_type: editing.policy.code,
        adjustment: delta,
        reason: editing.reason.trim(),
      });
      toast.success(`${editing.policy.name}: ${delta > 0 ? '+' : ''}${delta} day(s) ${delta > 0 ? 'added' : 'deducted'}`);
      setEditing(null);
      await fetchBalances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  const filtered = (data.employees || []).filter((e) => {
    const q = search.toLowerCase();
    return !q || (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  });

  return (
    <div data-testid="customize-balances-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-charcoal-900">Customize Leave Balances</h2>
          <p className="text-sm text-neutral-600 mt-1">
            Click any cell to add or subtract leaves. Changes are recorded as audit-logged
            transactions and reflect immediately for both the employee and admin.
          </p>
        </div>
        <Button onClick={fetchBalances} variant="outline" data-testid="refresh-balances-btn">
          Refresh
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-charcoal-200 shadow-sm">
        <div className="p-4 border-b border-charcoal-200">
          <Input
            placeholder="Search by employee name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="customize-balances-search"
            className="max-w-sm"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-charcoal-50 border-b border-charcoal-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-charcoal-700 sticky left-0 bg-charcoal-50 z-10">
                  Employee
                </th>
                {(data.policies || []).map((p) => (
                  <th key={p.code} className="px-3 py-3 text-center font-semibold text-charcoal-700 min-w-[90px]">
                    <div className="flex flex-col items-center gap-1">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: p.color || '#6366F1' }}
                      />
                      <span className="text-xs uppercase">{p.code}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-100">
              {loading && (
                <tr><td colSpan={(data.policies?.length || 0) + 1} className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={(data.policies?.length || 0) + 1} className="px-4 py-10 text-center text-neutral-500">No employees found</td></tr>
              )}
              {!loading && filtered.map((emp) => (
                <tr key={emp.employee_id} className="hover:bg-charcoal-25">
                  <td className="px-4 py-3 sticky left-0 bg-white z-10">
                    <div className="font-medium text-charcoal-900">{emp.name}</div>
                    <div className="text-xs text-neutral-500">{emp.email}</div>
                  </td>
                  {(data.policies || []).map((p) => {
                    const bal = emp.balances?.[p.code] || {};
                    const avail = bal.available ?? 0;
                    const isNeg = avail < 0;
                    const isLow = !isNeg && avail < 2 && (bal.allocated || 0) > 0;
                    return (
                      <td key={p.code} className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => openEditor(emp, p)}
                          className={`w-full rounded-lg px-2 py-1.5 transition hover:bg-pink-50 hover:ring-1 hover:ring-pink-200 ${
                            isNeg ? 'bg-red-50' : isLow ? 'bg-amber-50' : ''
                          }`}
                          data-testid={`balance-cell-${emp.employee_id}-${p.code}`}
                          title={`Allocated: ${bal.allocated || 0}  •  Used: ${bal.used || 0}  •  Pending: ${bal.pending || 0}  •  Carry fwd: ${bal.carry_forward || 0}  •  Manual adj: ${bal.manual_adjustments || 0}`}
                        >
                          <div className={`font-semibold ${isNeg ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-charcoal-900'}`}>
                            {Number(avail).toFixed(avail % 1 === 0 ? 0 : 1)}
                          </div>
                          <div className="text-[10px] text-neutral-400">/ {bal.allocated || 0}</div>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="adjust-balance-dialog">
          <DialogHeader>
            <DialogTitle>
              Adjust {editing?.policy?.name} for {editing?.emp?.name}
            </DialogTitle>
            <DialogDescription>
              Enter a positive number to credit extra days, or a negative number to deduct days.
              The adjustment is logged in the audit trail and applies to the current year ({data.year}).
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 p-3 bg-charcoal-50 rounded-lg text-sm">
                <div><span className="text-neutral-500">Allocated:</span> <span className="font-semibold">{editing.current.allocated || 0}</span></div>
                <div><span className="text-neutral-500">Used (yr):</span> <span className="font-semibold">{editing.current.used || 0}</span></div>
                <div><span className="text-neutral-500">Pending:</span> <span className="font-semibold">{editing.current.pending || 0}</span></div>
                <div><span className="text-neutral-500">Carry fwd:</span> <span className="font-semibold">{editing.current.carry_forward || 0}</span></div>
                <div><span className="text-neutral-500">Prev manual:</span> <span className="font-semibold">{editing.current.manual_adjustments || 0}</span></div>
                <div><span className="text-neutral-500">Available:</span>
                  <span className={`font-bold ml-1 ${(editing.current.available || 0) < 0 ? 'text-red-600' : 'text-charcoal-900'}`}>
                    {editing.current.available ?? 0}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal-700 mb-1">
                  Adjustment (days) — use negative to deduct
                </label>
                <Input
                  type="number"
                  step="0.5"
                  value={editing.delta}
                  onChange={(e) => setEditing({ ...editing, delta: e.target.value })}
                  placeholder="e.g. +2 or -1.5"
                  autoFocus
                  data-testid="adjust-delta-input"
                />
                {editing.delta && !isNaN(parseFloat(editing.delta)) && parseFloat(editing.delta) !== 0 && (
                  <p className="text-xs text-neutral-500 mt-1">
                    New available: <strong>{((editing.current.available || 0) + parseFloat(editing.delta)).toFixed(1)}</strong>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-charcoal-700 mb-1">Reason *</label>
                <Textarea
                  rows={2}
                  value={editing.reason}
                  onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                  placeholder="e.g. Comp off credit for Sept 10 shoot"
                  data-testid="adjust-reason-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={applyAdjustment}
              disabled={saving}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="confirm-adjustment-btn"
            >
              {saving ? 'Applying…' : 'Apply Adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
